import { extendVariants, DateInput as DateInputNext} from "@nextui-org/react";
import { CalendarDate, parseDate } from "@internationalized/date";
import { Controller } from "react-hook-form"
import {I18nProvider} from "@react-aria/i18n";

const DateInputVariant = extendVariants(DateInputNext, {
  variants: {
    size: {
      md: {
        inputWrapper: [
          'h-12'
        ],
        input: [
          'text-base'
        ],
        label: [
          'text-base'
        ],
        segment: [
          'text-base'
        ]
      }
    },
    color: {
      default: { // <- add a new color variant
        inputWrapper: [ // <- Input wrapper slot
          "bg-white w-auto",
          "border border-gray-500",
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[editable=true]:text-gray-800 text-base font-semibold",
          "data-[placeholder=true]:text-gray-800 text-base font-semibold",
          "data-[hover=true]:border-gray-500",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-gray-500",
          "group-data-[invalid=true]:border-red-400 group-data-[invalid=true]:bg-white group-data-[invalid=true]:hover:bg-white group-data-[invalid=true]:focus:bg-white group-data-[invalid=true]:focus-within:hover:bg-white"
        ],
        label: [
          "text-gray-500 font-normal text-sm",
          "group-data-[invalid=true]:text-gray-800"
        ],
        input: [  // <- Input element slot
          "text-gray-800 text-sm",
          "placeholder:text-gray-500",
          "group-data-[invalid=true]:text-gray-800"
        ],
        segment: [  // <- Input element slot
          "group-data-[invalid=true]:text-gray-800 group-data-[invalid=true]:focus:text-gray-800 group-data-[invalid=true]:focus:bg-default-400/50"
        ],
        errorMessage: [
          "font-normal"
        ]
      },
      primary: { // <- add a new color variant
        inputWrapper: [ // <- Input wrapper slot
          "bg-white w-auto text-gray-800",
          "border border-primary",
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[editable=true]:text-gray-800 text-base font-semibold",
          "data-[placeholder=true]:text-gray-800 text-base font-semibold",
          "data-[hover=true]:border-primary text-gray-800",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-primary text-gray-800",
          "group-data-[invalid=true]:border-red-400 group-data-[invalid=true]:bg-white group-data-[invalid=true]:hover:bg-white group-data-[invalid=true]:focus:bg-white group-data-[invalid=true]:focus-within:hover:bg-white"
        ],
        label: [
          "text-gray-500 font-normal text-sm",
          "group-data-[invalid=true]:text-gray-800"
        ],
        input: [  // <- Input element slot
          "text-gray-800 text-sm",
          "placeholder:text-gray-500",
          "group-data-[invalid=true]:text-gray-800"
        ],
        segment: [  // <- Input element slot
          "text-gray-800",
          "data-[placeholder=true]:text-gray-800 data-[editable=true]:text-gray-800",
          "group-data-[invalid=true]:text-gray-800 group-data-[invalid=true]:focus:text-gray-800 group-data-[invalid=true]:focus:bg-default-400/50"
        ],
      },
      custom: { // <- add a new color variant
        inputWrapper: [ // <- Input wrapper slot
          "bg-white",
          "border-b border-primary rounded-none",
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[editable=true]:text-gray-800 text-base font-semibold",
          "data-[placeholder=true]:text-gray-800 text-base font-semibold",
          "data-[hover=true]:border-primary",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-primary",
          "group-data-[invalid=true]:bg-white group-data-[invalid=true]:hover:bg-white group-data-[invalid=true]:focus:bg-white group-data-[invalid=true]:focus-within:hover:bg-white"
        ],
        innerWrapper: [ // <- Input wrapper slot
          "data-[editable=true]:text-gray-800 text-sm font-semibold",
          "data-[placeholder=true]:text-gray-800 text-sm font-semibold",
          "data-[hover=true]:border-primary",
          "data-[hover=true]:bg-white",
        ],
        label: [
          "text-primary font-semibold text-sm"
        ],
        input: [  // <- Input element slot
          "text-gray-600 text-sm font-semibold",
          "placeholder:text-gray-500",
        ],
        segment: [  // <- Input element slot
          "group-data-[invalid=true]:text-gray-800 group-data-[invalid=true]:focus:text-gray-800 group-data-[invalid=true]:focus:bg-default-400/50"
        ],
      },
      customDanger: { // <- add a new color variant
        inputWrapper: [ // <- Input wrapper slot
          "bg-white",
          "border-b border-danger rounded-none",
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[editable=true]:text-gray-500 text-base font-semibold",
          "data-[placeholder=true]:text-gray-500 text-base font-semibold",
          "data-[hover=true]:border-primary",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-danger",
          "group-data-[invalid=true]:bg-white group-data-[invalid=true]:hover:bg-white group-data-[invalid=true]:focus:bg-white group-data-[invalid=true]:focus-within:hover:bg-white"
        ],
        label: [
          "text-primary font-semibold text-sm",
          "group-data-[invalid=true]:text-primary"

        ],
        input: [  // <- Input element slot
          "text-gray-500 text-sm font-semibold",
          "placeholder:text-gray-500",
        ],
        segment: [  // <- Input element slot
          "text-gray-500",
          "group-data-[invalid=true]:text-gray-500 group-data-[invalid=true]:focus:text-gray-700 group-data-[invalid=true]:focus:bg-default-400/50"
        ],
      },
    },
  },
  defaultVariants: { // <- modify/add default variants
    color: "default",
    size: "md",
    radius: 'sm',
  },
});

const DateInput = props => {
  const { name, control, label = '', showLabel = false, ...rest } = props;
  return (
    <>   
      <Controller
        name={name}
        control={control}
        rules={{
          required: true,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <I18nProvider locale="es-ES">
            <DateInputVariant
              onChange={(value) => { 
                onChange(value?.toString() || null);
              }}
              onBlur={(value) => { 
                onBlur && onBlur(value?.toString() || null);
              }}
              placeholderValue={new CalendarDate(1995, 11, 6)} 
              {...rest} 
              label={showLabel ? label : null}
              aria-label={showLabel ? label : 'Date input'}
            />
          </I18nProvider>
        )}
       
      />
    </>
  );
};

export default DateInput;
